import type { ComponentProps } from "react";
import { generatePath, Link } from "react-router-dom";
import { t } from "ttag";

import { usePortalUserStore } from "@/app/stores";
import { CancelSubscriptionProvider } from "@/features/billing/context";
import { BILLING_ROUTE } from "@/features/billing/router";
import { CancelSubscriptionModals } from "@/features/billing/sections/cancel-subscription";
import { isInactiveSubscription } from "@/features/consultations/utils";
import { PaymentMethodDisplay } from "@/features/payments/components";
import { CATEGORY_COLOR } from "@/features/products/models";
import type { SubscriptionWithBillingDetails } from "@/shared/domains";
import { CATEGORY_SLUG, SUBSCRIPTION_VENDOR } from "@/shared/domains";
import { useBoolean, useMediaQuery } from "@/shared/hooks";
import { formatDate } from "@/shared/lib";
import {
  Badge,
  Button as ButtonPrimitive,
  DetailCard,
  MarkdownContent,
  WarningCircleIcon,
} from "@/shared/ui";
import { formatBackendDate, formatPrice } from "@/shared/utils";

const Button = ({ ...props }: ComponentProps<typeof ButtonPrimitive>) => {
  const isMobile = useMediaQuery();

  return (
    <ButtonPrimitive
      variant={isMobile ? "secondary" : "tertiary"}
      size="lg"
      className="md:p-0"
      {...props}
    />
  );
};

interface ActiveSubscriptionCardProps {
  subscription: SubscriptionWithBillingDetails;
}

export const ActiveSubscriptionCard = ({
  subscription,
}: ActiveSubscriptionCardProps) => {
  const isBlackFridayActive = usePortalUserStore(
    (state) => state.isBlackFridayActive,
  );

  const {
    id,
    currentProduct: product,
    cancelAt,
    vendor,
    isUpgradable,
    billingDetails: { paymentMethod, nextBillingAmount, nextBillingDate },
  } = subscription;

  const {
    value: showCancelSubscriptionModals,
    setTrue: openCancelSubscriptionModals,
    setFalse: closeCancelSubscriptionModals,
  } = useBoolean(false);

  const isWeightLoss = product.category.slug === CATEGORY_SLUG.WEIGHT_LOSS;

  const isFromOpenLoop = vendor === SUBSCRIPTION_VENDOR.OPEN_LOOP;

  const cancelDate = formatBackendDate(cancelAt, "MMM dd, yyyy");

  const canChangeSubscription =
    !isUpgradable && isWeightLoss && isFromOpenLoop && !isBlackFridayActive;

  return (
    <DetailCard.Root
      variant="borderLess"
      className="grid gap-y-2 bg-white md:grid-cols-[1fr_auto] md:gap-y-4"
    >
      <DetailCard.Header className="col-span-full flex-wrap items-center justify-between gap-x-2 pr-0 md:col-span-1 md:justify-normal">
        <DetailCard.Title className="leading-7">
          {product.name}
        </DetailCard.Title>

        <Badge size="small" color={CATEGORY_COLOR[product.category.slug]}>
          {product.category.name}
        </Badge>
      </DetailCard.Header>

      <DetailCard.Footer className="row-start-3 mt-2 flex flex-col-reverse gap-2 px-0 md:col-start-2 md:row-start-1 md:mt-0 md:flex-row md:gap-3 md:justify-self-end">
        {cancelAt && (
          <div className="flex items-center gap-1 self-center">
            <WarningCircleIcon className="size-3 text-red-700" />

            <span className="text-sm leading-7 text-red-700">
              {t`Active until ${cancelDate}`}
            </span>
          </div>
        )}

        {!cancelAt && (
          <>
            <Button onClick={openCancelSubscriptionModals} color="red">
              {t`Cancel`}
            </Button>

            {isUpgradable && (
              <Button asChild>
                <Link
                  to={generatePath(BILLING_ROUTE.UPGRADE_SUBSCRIPTION, {
                    subscriptionId: String(id),
                  })}
                >
                  {t`Upgrade`}
                </Link>
              </Button>
            )}

            {canChangeSubscription && (
              <Button asChild>
                <Link
                  to={BILLING_ROUTE.CHANGE_SUBSCRIPTION}
                  state={product.category}
                >
                  {t`Change`}
                </Link>
              </Button>
            )}
          </>
        )}
      </DetailCard.Footer>

      <DetailCard.Content className="col-span-full px-0">
        <div className="flex items-end justify-between gap-2">
          <DetailCard.Description
            title={product.description}
            className="line-clamp-2"
          >
            <MarkdownContent>{product.description}</MarkdownContent>
          </DetailCard.Description>

          <div className="flex flex-col-reverse items-center gap-2 whitespace-nowrap sm:flex-row">
            {isInactiveSubscription(subscription.status) && (
              <Badge color="red">{t`Payment failed`}</Badge>
            )}

            <DetailCard.Title className="font-medium">
              {formatPrice(product.price.price)}
            </DetailCard.Title>
          </div>
        </div>

        <DetailCard.Separator className="mx-0 bg-secondary-100" />

        <div className="flex items-center justify-between gap-2">
          <PaymentMethodDisplay paymentMethod={paymentMethod} />
          {Boolean(nextBillingAmount) && nextBillingDate && (
            <div className="flex flex-col items-end gap-1 sm:flex-row">
              <DetailCard.Label className="text-end text-sm text-primary-950">{t`$${nextBillingAmount} on next period`}</DetailCard.Label>
              <DetailCard.Label className="text-end text-sm text-primary-950">
                ({formatDate(nextBillingDate)})
              </DetailCard.Label>
            </div>
          )}
        </div>
      </DetailCard.Content>

      <CancelSubscriptionProvider
        show={showCancelSubscriptionModals}
        onClose={closeCancelSubscriptionModals}
        subscription={subscription}
      >
        <CancelSubscriptionModals />
      </CancelSubscriptionProvider>
    </DetailCard.Root>
  );
};
